/**
 * NotificationFilters Component
 *
 * Filter tabs for notifications (All, Urgent, Actions, Info, System).
 * Uses ButtonGroup with primary styling.
 */

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { useNotificationContext } from '@/context/NotificationContext';
import { cn } from '@/lib/utils';
import type { NotificationFilterType } from '@/types/notifications';

interface FilterConfig {
  value: NotificationFilterType;
  label: string;
  countKey?: 'urgent' | 'action_required' | 'info' | 'system';
}

const FILTERS: FilterConfig[] = [
  { value: 'all', label: 'All' },
  { value: 'urgent', label: 'Urgent', countKey: 'urgent' },
  { value: 'action_required', label: 'Actions', countKey: 'action_required' },
  { value: 'info', label: 'Info', countKey: 'info' },
  { value: 'system', label: 'System', countKey: 'system' },
];

export function NotificationFilters() {
  const { filter, setFilter, countByPriority, unreadCount, systemNotificationCount } = useNotificationContext();

  const getCount = (filterConfig: FilterConfig) => {
    if (filterConfig.countKey === 'system') {
      return systemNotificationCount;
    }
    if (filterConfig.countKey) {
      return countByPriority[filterConfig.countKey] || 0;
    }
    return unreadCount + systemNotificationCount;
  };

  return (
    <ButtonGroup className="w-full">
      {FILTERS.map((filterConfig) => {
        const count = getCount(filterConfig);
        const isActive = filter === filterConfig.value;

        return (
          <Button
            key={filterConfig.value}
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-7 px-2 text-xs font-medium flex-1',
              isActive && 'bg-primary text-primary-foreground hover:bg-primary/90',
              !isActive && 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setFilter(filterConfig.value)}
          >
            {filterConfig.label}
            {count > 0 && (
              <span
                className={cn(
                  'ml-1 inline-flex items-center justify-center',
                  'min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-semibold',
                  isActive
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {count > 99 ? '99+' : count}
              </span>
            )}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
